import { logError } from '@zg-rentals/log-error';
import ErrorMessageConstants from '../constants/ErrorMessageConstants';

interface LocationRestrictionProps {
  duplicateExists: boolean;
  duplicateSyndicatedUrl: string | null;
  forSaleExists: boolean;
  forSaleUrlOnZillow: string | null;
  isTopListingOwner: boolean;
  paidListingExists: boolean;
  paidMultifamilyLocation: boolean;
  restrictedFromNyc: boolean;
  setPropertyAlreadyPostedWarningMessage?: (data: string | Array<JSX.Element | string>) => void;
}

const getLocationRestrictionErrorMessage = (
  {
    duplicateExists,
    duplicateSyndicatedUrl,
    forSaleExists,
    forSaleUrlOnZillow,
    isTopListingOwner,
    paidListingExists,
    paidMultifamilyLocation,
    restrictedFromNyc,
  }: LocationRestrictionProps,
  isMultifamily = false,
  isMultifamilyCustomer = false,
  hasActiveMultifamilyContract = false,
  isSingleFamilyEnterpriseCustomer = false,
  isPostingPathAdmin = false,
  isHousingConnector = false,
  setPropertyAlreadyPostedWarningMessage = (data: string | Array<JSX.Element | string>) => {},
) => {
  const learnMoreElement = (
    <a
      href="https://www.zillow.help/article/why-didnt-my-listing-go-live-zd30234367973523"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more
    </a>
  );
  const propertyAlreadyPostedElements = (href: string) => [
    'This property is ',
    <a href={href} target="_blank" rel="noopener noreferrer">
      already posted
    </a>,
  ];
  const logErrorAndReturn = (message: Array<string | JSX.Element> | string) => {
    logError({
      error: new Error(typeof message === 'string' ? message : 'handleLocationRestrictionResponse error'),
      errorType: 'LocationRestrictionError',
      context: 'handleLocationRestrictionResponse',
    });
    return message;
  };

  setPropertyAlreadyPostedWarningMessage('');

  if (restrictedFromNyc) {
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.NYC;
    return logErrorAndReturn(message);
  }

  if (!isPostingPathAdmin && !isHousingConnector && paidMultifamilyLocation && !hasActiveMultifamilyContract) {
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED;
    return logErrorAndReturn(message);
  }

  if (paidListingExists && !isTopListingOwner) {
    return logErrorAndReturn(
      ErrorMessageConstants.PAID_LISTING_DUPE_ERROR(
        isMultifamily && isMultifamilyCustomer,
        isSingleFamilyEnterpriseCustomer,
      ),
    );
  }

  if (forSaleExists && !isMultifamily) {
    const postForSaleListingLearnMore = (
      <a
        href="https://www.zillow.help/article/advertising-for-sale-and-for-rent-listings-simultaneously-zd30237388907539"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more
      </a>
    );
    const message = forSaleUrlOnZillow
      ? [
          ...propertyAlreadyPostedElements(forSaleUrlOnZillow),
          ' for sale. You can post it for rent at the same time, but it may receive less visibility. ',
          postForSaleListingLearnMore,
        ]
      : [
          'This property was already posted for sale elsewhere. You can post it for rent at the same time, but it may receive less visibility. ',
          postForSaleListingLearnMore,
        ];
    return setPropertyAlreadyPostedWarningMessage(message);
  }

  if (duplicateExists) {
    if (duplicateSyndicatedUrl) {
      const message = [
        ...propertyAlreadyPostedElements(duplicateSyndicatedUrl),
        ' from another source. ',
        learnMoreElement,
      ];
      return setPropertyAlreadyPostedWarningMessage(message);
    }
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.DUPLICATE.MESSAGE;
    return setPropertyAlreadyPostedWarningMessage(message);
  }

  return null;
};

export default getLocationRestrictionErrorMessage;
